import { useState } from 'react'
import styled from 'styled-components'
import { CustomProvider, DateRangePicker } from 'rsuite'
const { afterToday } = DateRangePicker
import ptBR from 'rsuite/locales/pt_BR'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import {
  AutoCompleteWrapper,
  BarChartWrapper,
  ChartWithNoteWrapper,
  CustomChartLegend,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
} from '../../components'
import useViewport from '../../hooks/useViewport'
import filterDrawerHelper from '../../utils/filterDrawer'
import { RequestService } from '../../services/RequestService'
import {
  FatigueEvolutionProvider,
  GRANULARITY,
  useFatigueEvolution,
} from '../../contexts/FatigueEvolutionContext'
import { useCustomLegend } from '../../hooks'
import { useTranslation } from "react-i18next"

const Container = styled(Box)`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
`

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #f1f3f5;
  padding: 24px 44px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ChartContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
`

const FiltersTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const ReportPredictionWrapper = () => {
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const {
    data,
    loading,
    categories,
    selectedUser,
    onSelectUser,
    dateRange,
    onDateRangeChange,
    selectedFilterOptions,
    filterOptions,
    loadingFilters,
    onSelectFilter,
    onShift,
    onCheckOnShift,
    inputValue,
    onInputValueChange,
    granularity,
    onChangeGranularity,
    graphMessage,
  } = useFatigueEvolution()
  const { width } = useViewport()
  const { t } = useTranslation('common')
  const legends = [
    { label: t('reports.fatigue_evolution.legends.bad.text'), color: '#fd564e' },
    { label: t('reports.fatigue_evolution.legends.regular.text'), color: '#ffe000' },
    { label: t('reports.fatigue_evolution.legends.good.text'), color: '#62E751' },
  ]

  const initalLegendState = {
    bad: true,
    regular: true,
    good: true,
  }
  const { activeSeries, visibleSeries, toggleSeries, defineMax } =
    useCustomLegend({
      initalLegendState,
      data,
    })
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper

  const showDataLabels = () => {
    if (
      activeSeries[0]?.data?.length >
      Math.max(5, Math.floor((width - 450) / 50) + 5)
    )
      return false
    return true
  }

  return (
    <Container>
      <Body>
        <Title>
          <span style={{ fontSize: 20, fontWeight: 600 }}>
            {t('reports.fatigue_evolution.title')}
          </span>
          <Tooltip
            title={t('reports.fatigue_evolution.tooltip')}
            placement='right'
            arrow
          >
            <HelpOutline fontSize='24' />
          </Tooltip>
        </Title>
        <ChartContainer>
          <FiltersTagsContainer>
            <FiltersContainer>
              <div style={{ display: 'flex', flex: 1 }}>
                <AutoCompleteWrapper
                  onLoad={() => RequestService.getUsers({ visibility: 'reports' })}
                  value={selectedUser}
                  onChangeValue={onSelectUser}
                  inputValue={inputValue}
                  onInputValueChange={onInputValueChange}
                />
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    gap: 16,
                  }}
                >
                  <CustomProvider locale={ptBR}>
                    <DateRangePicker
                      defaultCalendarValue={dateRange}
                      value={dateRange}
                      onChange={event => onDateRangeChange(event)}
                      showOneCalendar
                      shouldDisableDate={afterToday()}
                      character=' - '
                      placement='bottom'
                      placeholder={t('reports.fatigue_evolution.placeholder')}
                      ranges={[]}
                      cleanable={false}
                    />
                  </CustomProvider>
                  <FormControl
                    style={{ display: 'flex', flex: 1, minWidth: 120 }}
                  >
                    <InputLabel id='group-by'>{t('reports.fatigue_evolution.interval')}</InputLabel>

                    <Select
                      labelId='group-by-label'
                      id='group-by'
                      label={t('reports.fatigue_evolution.interval')}
                      value={granularity}
                      onChange={onChangeGranularity}
                      size='small'
                    >
                      <MenuItem value={GRANULARITY.DAILY}>Diário</MenuItem>
                      <MenuItem value={GRANULARITY.WEEKLY}>Semanal</MenuItem>
                      <MenuItem value={GRANULARITY.MONTHLY}>Mensal</MenuItem>
                      <MenuItem value={GRANULARITY.ANNUALLY}>Anual</MenuItem>
                    </Select>
                  </FormControl>
                  <Filter onClick={() => setOpenFilterDrawer(true)} />
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={onShift === t('yes')}
                      onChange={() =>
                        onCheckOnShift(onShift === t('yes') ? t('no') : t('yes'))
                      }
                    />
                  }
                  label={t('reports.fatigue_evolution.on_shift')}
                />
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            })?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <GroupedFilterTags
                  tags={selectedFilterOptionsList({
                    selectedFilterOptions,
                  })}
                  onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                  maxTagsToShow={getMaxTagsToShow({ width })}
                />
              </div>
            )}
          </FiltersTagsContainer>

          <ChartWithNoteWrapper
            totalHours={graphMessage?.totalHours}
            totalUsers={graphMessage?.totalUsers}
          >
            {!loading && (
              <BarChartWrapper
                data={activeSeries}
                legend={{ show: false }}
                categories={categories}
                colors={data.map(s => s.color)}
                yProps={{
                  ...defineMax(),
                }}
                dataLabelsProps={
                  showDataLabels() ? { enabled: true } : { enabled: false }
                }
                formatter={val => Math.round(val) + '%'}
              />
            )}

            <CustomChartLegend
              visibleSeries={visibleSeries}
              toggleSeries={toggleSeries}
              legends={legends}
            />
          </ChartWithNoteWrapper>
        </ChartContainer>
      </Body>

      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={onSelectFilter}
        loading={loadingFilters}
        options={filterOptions}
        withShift
        withPosition
        withTeam
      />
    </Container>
  )
}

const ReportFatigueEvolution = () => {
  return (
    <FatigueEvolutionProvider>
      <ReportPredictionWrapper />
    </FatigueEvolutionProvider>
  )
}

export default ReportFatigueEvolution
